import { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// Components
import { Button, DateRangePopover, Modal } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import { versionBasePath } from "components/VersionManagementButtons";

// Utils
import { extractErrorMessage } from "utils/errors";

// State
import {
  useGetAvailableEMSModulesQuery,
  useGetEMSConfigsQuery,
  useTriggerEMSCalculationMutation,
  useDeleteEMSConfigMutation,
} from "state/communities/communitiesApiSlice";
import { useGetMyPermissionsQuery } from "state/auth/authApiSlice";
import { selectRole } from "state/auth/authSlice";
import {
  openNotificationPopup,
  openSuccessAlert,
  openWarningAlert,
} from "state/application/applicationSlice";

// Sub-components
import ModuleCard from "./ModuleCard";
import ModuleConfigForm from "./ModuleConfigForm";

// Poll cadence while a module calculation is RUNNING, and a hard ceiling after
// which polling stops even if the task never reports a terminal state.
const EMS_POLL_INTERVAL_MS = 5000;
const EMS_POLL_MAX_MS = 10 * 60 * 1000;

function getTimeSince(dateStr, t) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return t("unified_ems.just_now");
  if (minutes < 60) return t("unified_ems.minutes_ago", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("unified_ems.hours_ago", { count: hours });
  const days = Math.floor(hours / 24);
  return t("unified_ems.days_ago", { count: days });
}

function UnifiedEMS({ communityId }) {
  const { t } = useTranslation(["community"]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Config modal: { moduleType, config, isEditMode } or null
  const [configModal, setConfigModal] = useState(null);

  // Date picker dialog state (for day-ahead modules like OSTEC_PROMOTOR)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split("T")[0];

  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [dateDialogConfigId, setDateDialogConfigId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(tomorrowStr);

  const [triggerCalculation] = useTriggerEMSCalculationMutation();
  const [deleteConfig] = useDeleteEMSConfigMutation();

  // Config whose calculation request is in-flight (HTTP-level), so the spinner
  // shows immediately, before the backend reports RUNNING on the next refetch.
  const [calculatingConfigId, setCalculatingConfigId] = useState(null);

  // Conditional polling: only poll while a module is RUNNING, pause when the tab
  // is hidden, and auto-stop after a safety timeout (see effects below).
  const [pollInterval, setPollInterval] = useState(0);
  const [isTabVisible, setIsTabVisible] = useState(
    typeof document === "undefined" ? true : !document.hidden
  );

  // Fetch available modules and active configurations
  const {
    data: modulesData,
    isLoading: isLoadingModules,
    error: modulesError,
  } = useGetAvailableEMSModulesQuery(communityId);

  const {
    data: configs,
    isLoading: isLoadingConfigs,
    error: configsError,
  } = useGetEMSConfigsQuery(communityId, { pollingInterval: pollInterval });

  const { data: myPermissions } = useGetMyPermissionsQuery();
  const currentRole = useSelector(selectRole);

  const allModules = modulesData?.modules || [];

  // SUPER_USER sees all modules; other roles see only those with active EMS_* permission
  const availableModules = (() => {
    if (currentRole === "SUPER_USER") return allModules;
    if (!myPermissions) return allModules;
    const allowed = new Set(
      myPermissions
        .filter((p) => p.name.startsWith("EMS_") && p.is_active)
        .map((p) => p.name.replace(/^EMS_/, ""))
    );
    // If user has no EMS permissions at all yet, show all (graceful fallback)
    if (allowed.size === 0 && !myPermissions.some((p) => p.name.startsWith("EMS_"))) {
      return allModules;
    }
    return allModules.filter((m) => allowed.has(m.module_type));
  })();
  const activeConfigs = configs || [];
  const activeModuleTypes = activeConfigs.map((config) => config.module_type);

  const activeModules = availableModules.filter((module) =>
    activeModuleTypes.includes(module.module_type)
  );
  const inactiveModules = availableModules.filter(
    (module) => !activeModuleTypes.includes(module.module_type)
  );

  const configFor = (moduleType) => activeConfigs.find((c) => c.module_type === moduleType);

  // Stable module display-name lookup, used to label completion toasts.
  const moduleNameByType = useMemo(() => {
    const map = {};
    (modulesData?.modules || []).forEach((m) => {
      map[m.module_type] = m.display_name;
    });
    return map;
  }, [modulesData]);

  // A module is "calculating" while its trigger request is in-flight or while
  // the backend reports it as RUNNING in the background.
  const isConfigCalculating = (config) =>
    !!config && (calculatingConfigId === config.id || config.last_run_status === "RUNNING");

  // Track tab visibility so polling pauses while the tab is hidden.
  useEffect(() => {
    const onVisibility = () => setIsTabVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Conditional polling: poll only while a module is RUNNING and the tab is
  // focused; otherwise stop so no requests fire when idle.
  useEffect(() => {
    const anyRunning = (configs || []).some((c) => c.last_run_status === "RUNNING");
    setPollInterval(anyRunning && isTabVisible ? EMS_POLL_INTERVAL_MS : 0);
  }, [configs, isTabVisible]);

  // Safety auto-stop: never poll longer than EMS_POLL_MAX_MS, in case a task is
  // stuck in RUNNING and never reaches a terminal state.
  useEffect(() => {
    if (pollInterval === 0) return undefined;
    const id = setTimeout(() => setPollInterval(0), EMS_POLL_MAX_MS);
    return () => clearTimeout(id);
  }, [pollInterval]);

  // Detect RUNNING -> SUCCESS/FAILED transitions and surface a completion toast.
  const prevStatusRef = useRef(null);
  useEffect(() => {
    const current = {};
    (configs || []).forEach((c) => {
      current[c.id] = c.last_run_status;
    });
    const prev = prevStatusRef.current;
    // Skip the first run so we don't toast for an already-finished module on mount.
    if (prev) {
      (configs || []).forEach((c) => {
        const finished =
          prev[c.id] === "RUNNING" && c.last_run_status && c.last_run_status !== "RUNNING";
        if (!finished) return;
        const moduleName = moduleNameByType[c.module_type] || c.module_type;
        if (c.last_run_status === "SUCCESS") {
          dispatch(
            openNotificationPopup({
              notification: t("unified_ems.calculation_complete", { module: moduleName }),
              type: "success",
            })
          );
        } else if (c.last_run_status === "FAILED") {
          dispatch(
            openNotificationPopup({
              notification: t("unified_ems.calculation_failed", { module: moduleName }),
              type: "error",
            })
          );
        }
      });
    }
    prevStatusRef.current = current;
  }, [configs, moduleNameByType, dispatch, t]);

  const triggerCalculationConfirmed = async (configId, targetDate) => {
    setCalculatingConfigId(configId);
    const cfg = activeConfigs.find((c) => c.id === configId);
    const moduleName = (cfg && moduleNameByType[cfg.module_type]) || "";
    try {
      const res = await triggerCalculation({ communityId, configId, targetDate }).unwrap();
      if (res?.async) {
        // Dispatched to a background worker: report it started; the polling
        // effect surfaces the completion toast on RUNNING -> terminal.
        dispatch(
          openNotificationPopup({
            notification: t("unified_ems.calculation_triggered_desc"),
            type: "success",
          })
        );
      } else {
        // Synchronous module: the request only resolves once it has finished.
        dispatch(
          openNotificationPopup({
            notification: t("unified_ems.calculation_complete", { module: moduleName }),
            type: "success",
          })
        );
      }
    } catch (err) {
      // Surface the failure instead of swallowing it silently.
      dispatch(
        openNotificationPopup({
          notification: extractErrorMessage(err) || t("unified_ems.error_saving"),
          type: "error",
        })
      );
    } finally {
      // Drop the HTTP-level flag; the RUNNING status now keeps the spinner alive
      // until the background task reports a terminal state.
      setCalculatingConfigId(null);
    }
  };

  const handleCalculate = (module) => {
    const config = configFor(module.module_type);
    const isDayAhead = module?.capabilities?.includes("day_ahead");
    if (isDayAhead) {
      setDateDialogConfigId(config?.id);
      setSelectedDate(tomorrowStr);
      setDateDialogOpen(true);
    } else {
      triggerCalculationConfirmed(config?.id, null);
    }
  };

  const handleDateDialogConfirm = () => {
    setDateDialogOpen(false);
    triggerCalculationConfirmed(dateDialogConfigId, selectedDate);
  };

  const handleDeactivate = (module) => {
    const config = configFor(module.module_type);
    if (!config?.id) return;
    dispatch(
      openWarningAlert({
        title: t("unified_ems.confirm_delete_title"),
        text: t("unified_ems.confirm_delete_text"),
        confirmText: t("unified_ems.confirm_delete_button"),
        isDangerous: true,
        icon: "warning",
        confirmAction: async () => {
          try {
            await deleteConfig({ communityId, configId: config.id }).unwrap();
            dispatch(
              openSuccessAlert({
                title: t("unified_ems.delete_success_title"),
                description: t("unified_ems.delete_success_desc"),
              })
            );
          } catch (error) {
            dispatch(
              openNotificationPopup({
                notification:
                  error?.data?.detail || error.message || t("unified_ems.error_deleting"),
                type: "error",
              })
            );
          }
        },
      })
    );
  };

  const modalModule = configModal
    ? availableModules.find((m) => m.module_type === configModal.moduleType)
    : null;

  if (isLoadingModules || isLoadingConfigs) {
    return (
      <div className="flex justify-center items-center min-h-[200px] text-ink-3">
        <span className="animate-spin inline-flex">
          <Icon name="progress_activity" style={{ fontSize: 32, color: "var(--accent)" }} />
        </span>
      </div>
    );
  }

  if (modulesError || configsError) {
    return (
      <div className="text-[13px] text-danger py-4">
        {t("unified_ems.error_loading")}: {modulesError?.message || configsError?.message}
      </div>
    );
  }

  return (
    <div>
      {/* Active modules */}
      {activeModules.length > 0 && (
        <div className="mb-6">
          <div className="rec-ems-section-title">
            <Icon name="check_circle" style={{ fontSize: 18, color: "var(--success)" }} />
            {t("unified_ems.active_modules")}
            <span className="rec-ems-count">{activeModules.length}</span>
          </div>
          <div className="rec-ems-grid">
            {activeModules.map((module) => {
              const config = configFor(module.module_type);
              return (
                <ModuleCard
                  key={module.module_type}
                  module={module}
                  isActive
                  config={config}
                  lastRunLabel={
                    config?.last_run_at
                      ? `${t("unified_ems.last_run")}: ${getTimeSince(config.last_run_at, t)}`
                      : null
                  }
                  isCalculating={isConfigCalculating(config)}
                  onCalculate={() => handleCalculate(module)}
                  onConfigure={() =>
                    navigate(versionBasePath(communityId, "ems", config.id), {
                      state: {
                        label: module.display_name,
                        returnTo: `/communities/${communityId}/configuration`,
                      },
                    })
                  }
                  onDeactivate={() => handleDeactivate(module)}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Available modules */}
      {inactiveModules.length > 0 && (
        <div>
          <div className="rec-ems-section-title">
            <Icon name="widgets" style={{ fontSize: 18, color: "var(--ink-3)" }} />
            {t("unified_ems.available_modules")}
          </div>
          <div className="rec-ems-grid">
            {inactiveModules.map((module) => (
              <ModuleCard
                key={module.module_type}
                module={module}
                onActivate={() =>
                  setConfigModal({
                    moduleType: module.module_type,
                    config: null,
                    isEditMode: false,
                  })
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* No modules */}
      {availableModules.length === 0 && (
        <div className="flex flex-col items-center gap-[10px] py-[48px] text-ink-3 rec-card">
          <Icon name="settings" style={{ fontSize: 42, opacity: 0.6 }} />
          <div className="text-[14px] font-semibold">{t("unified_ems.no_modules")}</div>
          <div className="text-[12.5px] max-w-[420px] text-center">
            {t("unified_ems.no_modules_description")}
          </div>
        </div>
      )}

      {/* Module config modal (activate / configure) */}
      <Modal
        open={Boolean(configModal && modalModule)}
        onClose={() => setConfigModal(null)}
        title={modalModule?.display_name}
        subtitle={t("unified_ems.configure_module")}
        maxWidth={720}
      >
        {configModal && modalModule && (
          <ModuleConfigForm
            communityId={communityId}
            moduleType={configModal.moduleType}
            config={configModal.config}
            isEditMode={configModal.isEditMode}
            onCancel={() => setConfigModal(null)}
            onSuccess={() => setConfigModal(null)}
          />
        )}
      </Modal>

      {/* Day-ahead date picker modal */}
      <Modal
        open={dateDialogOpen}
        onClose={() => setDateDialogOpen(false)}
        title={t("unified_ems.select_target_date")}
        subtitle={t("unified_ems.day_ahead_hint")}
        maxWidth={420}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDateDialogOpen(false)}>
              {t("unified_ems.cancel")}
            </Button>
            <Button
              disabled={!selectedDate || calculatingConfigId === dateDialogConfigId}
              onClick={handleDateDialogConfirm}
            >
              <Icon name="play_arrow" />
              {t("unified_ems.calculate_now")}
            </Button>
          </>
        }
      >
        <div className="rec-field">
          <span className="text-[12.5px] font-semibold text-ink-2">
            {t("unified_ems.target_date")}
          </span>
          <div className="self-start">
            <DateRangePopover
              mode="single"
              allowFuture
              value={{ start: selectedDate, end: selectedDate }}
              onChange={({ start }) => setSelectedDate(start)}
              ariaLabel={t("unified_ems.target_date")}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

UnifiedEMS.propTypes = {
  communityId: PropTypes.string.isRequired,
};

export default UnifiedEMS;
